import { useState } from 'react';
import { Button, IconButton, Stack, TextField, Tooltip, MenuItem, Snackbar, Alert } from '@mui/material';
import { Add, Visibility, Delete, Autorenew, Warning } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../../components/PageHeader';
import { ExportMenu } from '../../../components/ExportMenu';
import { DataTable, type Column } from '../../../components/DataTable';
import { StatusBadge } from '../../../components/StatusBadge';
import { ConfirmDialog } from '../../../components/ConfirmDialog';
import { AmcContractFormDialog } from './AmcContractFormDialog';
import { AmcContractDetailDialog } from './AmcContractDetailDialog';
import { evcpdAmcApi, type AmcContract, type CreateContractInput, type AmcStatus } from '../../../api/evcpdAmc.api';
import { extractApiError } from '../../../api/error';
import { useAuth } from '../../../hooks/useAuth';

const STATUSES: AmcStatus[] = ['draft', 'pending_approval', 'active', 'expiring', 'expired', 'renewed', 'cancelled', 'rejected'];
const inr = (n = 0) => `₹${Number(n).toLocaleString('en-IN')}`;

export function AmcContractListPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [status, setStatus] = useState('');
  const [pmStatus, setPmStatus] = useState('');
  const [pmDueInDays, setPmDueInDays] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<AmcContract | null>(null);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState<AmcContract | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const { data, isLoading } = useQuery({ queryKey: ['amc-contracts', page, limit, status], queryFn: () => evcpdAmcApi.list({ page, limit, status: status || undefined }) });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['amc-contracts'] });

  const createMut = useMutation({
    mutationFn: (input: CreateContractInput) => evcpdAmcApi.create(input),
    onSuccess: (c) => { setFormOpen(false); invalidate(); setDetailId(c.id); },
    onError: (e) => setFormError(extractApiError(e)),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, input }: { id: number; input: CreateContractInput }) => evcpdAmcApi.update(id, input),
    onSuccess: () => { setFormOpen(false); setEditing(null); invalidate(); },
    onError: (e) => setFormError(extractApiError(e)),
  });
  const deleteMut = useMutation({ mutationFn: (id: number) => evcpdAmcApi.remove(id), onSuccess: () => { setDeleting(null); invalidate(); }, onError: (e) => { setToast(extractApiError(e)); setDeleting(null); } });
  const generateMut = useMutation({ mutationFn: () => evcpdAmcApi.generatePm(7), onSuccess: (r) => { setToast(`Generated ${r.created} PM ticket(s).`); invalidate(); }, onError: (e) => setToast(extractApiError(e)) });
  const escalateMut = useMutation({ mutationFn: () => evcpdAmcApi.escalate(), onSuccess: (r) => setToast(`${r.missed} missed PM escalated.`), onError: (e) => setToast(extractApiError(e)) });
  const renewMut = useMutation({ mutationFn: () => evcpdAmcApi.runRenewals(), onSuccess: (r) => setToast(`${r.sent} renewal reminder(s) sent.`), onError: (e) => setToast(extractApiError(e)) });

  const columns: Column<AmcContract>[] = [
    { key: 'amcCode', label: 'AMC', width: 170 },
    { key: 'contractType', label: 'Type', render: (r) => r.contractType.replace(/_/g, ' ') },
    { key: 'pmFrequency', label: 'PM', render: (r) => r.pmFrequency.replace(/_/g, ' ') },
    { key: 'contractValue', label: 'Value', align: 'right', render: (r) => inr(r.contractValue) },
    { key: 'amcEndDate', label: 'Ends', render: (r) => r.amcEndDate ?? '—' },
    { key: 'effectiveStatus', label: 'Status', render: (r) => <StatusBadge status={r.effectiveStatus} /> },
  ];

  return (
    <>
      <PageHeader
        title="AMC Contracts"
        subtitle="AMC & preventive-maintenance contracts with auto PM scheduling."
        action={
          <Stack direction="row" spacing={1}>
            <ExportMenu dataset="amc" permission="amc.export" filters={{ status, pmCompletionStatus: pmStatus, pmDueInDays }} />
            {hasPermission('evcpd_pm.generate') && <Button variant="outlined" onClick={() => generateMut.mutate()} disabled={generateMut.isPending}>Generate Due PM</Button>}
            {hasPermission('evcpd_pm.escalate') && <Button variant="outlined" color="warning" startIcon={<Warning />} onClick={() => escalateMut.mutate()} disabled={escalateMut.isPending}>Run SLA Sweep</Button>}
            {hasPermission('evcpd_pm.manage') && <Button variant="outlined" onClick={() => renewMut.mutate()} disabled={renewMut.isPending}>Run Renewals</Button>}
            {hasPermission('evcpd_amc_contract.create') && <Button startIcon={<Add />} variant="contained" onClick={() => { setEditing(null); setFormError(null); setFormOpen(true); }}>New AMC</Button>}
          </Stack>
        }
      />
      <Stack direction="row" spacing={2} mb={2} flexWrap="wrap" useFlexGap>
        <TextField select size="small" label="Status" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} sx={{ minWidth: 200 }}>
          <MenuItem value="">All</MenuItem>
          {STATUSES.map((s) => <MenuItem key={s} value={s}>{s.replace(/_/g, ' ')}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="PM status" value={pmStatus} onChange={(e) => setPmStatus(e.target.value)} sx={{ minWidth: 160 }}>
          <MenuItem value="">All</MenuItem>
          {['not_scheduled', 'up_to_date', 'due', 'overdue'].map((s) => <MenuItem key={s} value={s}>{s.replace(/_/g, ' ')}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="PM due in" value={pmDueInDays} onChange={(e) => setPmDueInDays(e.target.value)} sx={{ minWidth: 140 }}>
          <MenuItem value="">Any</MenuItem>
          {[30, 60, 90].map((d) => <MenuItem key={d} value={d}>{d} days</MenuItem>)}
        </TextField>
      </Stack>
      <Alert severity="info" sx={{ mb: 2 }}>PM status / PM-due filters apply to the Excel/CSV/PDF export.</Alert>
      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        loading={isLoading}
        page={page}
        limit={limit}
        total={data?.meta.total ?? 0}
        onPageChange={setPage}
        onLimitChange={(l) => { setLimit(l); setPage(1); }}
        rowKey={(r) => r.id}
        actions={(r) => (
          <>
            <Tooltip title="Open"><IconButton size="small" onClick={() => setDetailId(r.id)}><Visibility fontSize="small" /></IconButton></Tooltip>
            {hasPermission('evcpd_amc_contract.update') && r.status === 'draft' && <Tooltip title="Edit"><IconButton size="small" onClick={() => { setEditing(r); setFormError(null); setFormOpen(true); }}><Autorenew fontSize="small" /></IconButton></Tooltip>}
            {hasPermission('evcpd_amc_contract.delete') && ['draft', 'rejected', 'cancelled'].includes(r.status) && <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleting(r)}><Delete fontSize="small" /></IconButton></Tooltip>}
          </>
        )}
      />

      <AmcContractFormDialog
        open={formOpen}
        initial={editing}
        error={formError}
        onClose={() => setFormOpen(false)}
        onSubmit={async (input) => { setFormError(null); if (editing) await updateMut.mutateAsync({ id: editing.id, input }); else await createMut.mutateAsync(input); }}
      />
      <AmcContractDetailDialog contractId={detailId} onClose={() => setDetailId(null)} onChanged={invalidate} />
      <ConfirmDialog open={Boolean(deleting)} title="Delete AMC" message={`Delete ${deleting?.amcCode}?`} confirmLabel="Delete" onCancel={() => setDeleting(null)} onConfirm={() => deleting && deleteMut.mutate(deleting.id)} />
      <Snackbar open={Boolean(toast)} autoHideDuration={6000} onClose={() => setToast(null)}><Alert severity="info" onClose={() => setToast(null)}>{toast}</Alert></Snackbar>
    </>
  );
}
